<style>
.card-limit-title-container {
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
}
.card-limit-title {
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    width: 100%;
    display: block;
    font-size: 1.125rem;
    font-weight: 500;
}
</style>

<template>
    <v-hover v-slot="{ hover }">
        <v-card class="d-flex fill-height flex-column"
            :elevation="hover ? 12 : 2"
            style="cursor: pointer; height: fit-content;"
            @click="() => $emit('productClick', product)"
            :ripple="false" >

            <v-auto-img v-if="!cropImage"
                :src="product.imagekey ? product.imagekey : undefined"
                position="center center"
                :ar="3 / 2" height="50%"
                class="blur-img py-2 align-center" >
                <v-auto-img contain
                    :src="product.imagekey ? product.imagekey : undefined"
                    position="center center"
                    :ar="3 / 2" >
                    <v-avatar v-if="product.sites && product.sites.length > 1"
                        class="ma-2"
                        style="float: right"
                        color="rgba(0,0,0,0.5)" size="30" >
                        <v-icon color="white">mdi-map-marker-star-outline</v-icon>
                    </v-avatar>
                </v-auto-img>
            </v-auto-img>

            <v-auto-img v-else
                :src="product.imagekey ? product.imagekey : undefined"
                position="center center"
                :ar="3 / 2" >
                <v-avatar v-if="product.sites && product.sites.length > 1"
                    class="ma-2"
                    style="float: right"
                    color="rgba(0,0,0,0.5)"
                    size="30" >
                    <v-icon color="white">mdi-map-marker-star-outline</v-icon>
                </v-avatar>
            </v-auto-img>

            <v-row class="mb-2">
                <v-col cols="12">
                    <v-card-title class="card-limit-title-container pa-2 pa-sm-4">
                        <span class="card-limit-title" :style="titleStyle">
                            {{ product.name }}
                        </span>
                    </v-card-title>
                </v-col>

                <v-col class="px-2 mx-3">
                    <v-row class="align-end" :style="infoStyle">
                        <v-col v-if="product.trainingsessions"
                            class="py-0"
                            cols="12" sm="auto"
                        >
                            <v-icon small color="primary">mdi-ticket</v-icon>
                            {{ product.trainingsessions }} {{ $translate('Punch tickets').toLowerCase() }}
                        </v-col>

                        <v-col v-else-if="product.validTime"
                            class="py-0"
                            cols="12" sm="auto"
                        >
                            <template v-if="product.membership">
                                <v-icon small color="primary">
                                    {{ product.auto ? "mdi-update" : "mdi-calendar-clock"}}
                                </v-icon>
                                <template v-if="!product.validFromDate">
                                    {{ product.validTime }} {{ $translate(product.validType + (product.validTime === 1 ? '' : 's')).toLowerCase()}}
                                </template>
                                <template v-else>
                                    <template>
                                        {{
                                            isMembershipFrom0101(product) ?
                                                $translate('for').capitalize() + " " + new Date().getFullYear() :
                                                " " + $translate('To') + " " + calculateAndFormatEndDate(product.validFromDate)
                                        }}
                                    </template>
                                </template>
                            </template>
                            <template v-else>
                                <v-icon small color="primary">mdi-calendar-clock</v-icon>
                                {{ product.validTime }} {{ $translate(product.validType + (product.validTime === 1 ? '' : 's')).toLowerCase()}}
                            </template>
                        </v-col>

                        <v-col v-else-if="needsToChooseVariant(product)"
                            class="py-0"
                            cols="12" sm="auto"
                        >
                            {{ product.variants.length }} {{ $translate('variants') }}
                        </v-col>

                        <v-col v-if="!product.payWhatYouWant"
                            class="py-0 ml-auto text-right"
                            cols="12" sm="auto"
                        >
                            <template v-if="discountedPrice">
                                <span class="zoezi-original-price text--secondary">
                                    {{ discountedPrice.base_price | price }}
                                </span>
                                <span class="primary--text product-price ml-1">
                                    <v-icon small color="primary">mdi-tag</v-icon>
                                    {{ discountedPrice.final_price | price }}
                                </span>
                            </template>
                            <template v-else>
                                <span class="primary--text product-price">
                                    <v-icon small color="primary">mdi-tag</v-icon>
                                    {{ product.price | price }}
                                </span>
                            </template>
                            <span v-if="product.priceType && product.priceType != 'OneTime'">
                                /{{ $translate(product.priceType).toLowerCase() }}
                            </span>
                            <div v-if="$api.hasFlag('flag-one-time-fee') && product.onetime_fee_product" class="text-caption">
                                + {{ product.onetime_fee_product.price | price }} {{ product.onetime_fee_product.name }}
                            </div>
                            <div v-if="discountedPrice && discountedPrice.rules && discountedPrice.rules.length" class="text--secondary" style="font-size: 0.75em">
                                {{ discountedPrice.rules[0].name }}
                            </div>
                        </v-col>
                    </v-row>
                </v-col>
            </v-row>

            <v-card-actions>
                <v-btn class="zoezi-button-thin"
                    block color="primary"
                    @click.stop="() => $emit('buyClick', product)" >
                    {{ $translate('Buy') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-hover>
</template>

<script>
import priceRulePricing from '../../mixins/priceRulePricing';

export default {
    name: "ShopItemCard",
    mixins: [priceRulePricing],
    props: {
        product: Object,
        cropImage: {
            type: Boolean,
            default: true,
        },
    },
    methods: {
        calculateAndFormatEndDate(date) {
            let dateObj = Date.newFull(date);
            dateObj.setDate(dateObj.getDate() - 1);
            return Date.formatFullShorter(dateObj);
        },
        isMembershipFrom0101(product) {
            return product.validFromDate.slice(5) == '01-01';
        },
        needsToChooseVariant(product) {
            if (!product.variants || !product.variants.length) {
                return false;
            }
            const variants = product.variants;
            if (variants.length > 1) {
                return true;
            }
            const firstVariant = variants[0];
            return firstVariant.name && firstVariant.name !== 'Standard';
        },
    },
    computed: {
        titleStyle() {
            return {
                height: this.$vuetify.breakpoint.sm ? "6em" : "1.5em",
                width: "100%",
                "text-overflow": "ellipsis",
            }
        },
        infoStyle() {
            if (this.$vuetify.breakpoint.sm) return {};
            return {
                "min-height": "3em",
            };
        },
    }
};
</script>
